// @ts-strict-ignore
import queryString from 'query-string';
import stableStringify from 'json-stable-stringify';
import omit from 'lodash/omit';
import {v4 as uuidv4} from 'uuid';
import {context as otel_context, ROOT_CONTEXT} from '@opentelemetry/api';

import {getProjectId} from 'common/report/util';

import {reporter} from '../report/error-reporting';
import {isBrowserEnv, isDevEnv, isLocalhostDevEnv, isMixpanelDotCom, isStandalone, isTestEnv} from '../report/util/env';
import {userCookieUtils} from '../report/util/user-identity-from-cookie';
import {
  filterSearchParamsForCache,
  fromFetchCache,
  setCacheNamespace,
  updateFetchCache,
} from '../report/queries/utils/fetch-cache';
import {
  apiHost,
  FetchOptions,
  HttpClient,
  isSameOrigin,
  makeError,
  ResponseError,
  StatusCode,
  trackApiClientFetch,
  waitForRedirect,
} from './shared-client';
import {sleep} from './dom';
import {reportQueryApiRequestsToWrongRegion, trackQueryApiFetch} from './query-api-wrong-region-reporter';
import {getCachedProjectDomain} from './get-cached-project-domain';

export const MAX_AUTH_RETRIES = 3;

function accessTokenFromURLHash() {
  return isBrowserEnv() ? queryString.parse(window.location.hash)[`access_token`] : null;
}

// ----- standalone access token helpers ---- ///

interface OAuthState {
  numRedirectAttempts: number;
  originalHash: string;
}

function buildOAuthRedirectUrl(parsedState: OAuthState): string {
  // Mixpanel.com client ID with implicit authorization grant type
  // see oauth2_provider_application table for full configuration
  const mixpanelClientId = `LJzdnKoilgp50qtCl1yZXuwwzlv5CgP9ung4MN0x`;
  const redirectPath = window.location.origin + window.location.pathname;
  const urlQueryParams = omit(queryString.parse(window.location.search), `state`);
  const redirectUri = `${redirectPath}?${queryString.stringify(urlQueryParams)}`;
  const redirectState = {...parsedState, originalHash: window.location.hash};

  // keep in sync with configurations/settings_global.py -> _SCOPES, otherwise invalid_scope error is thrown
  const oauthScopes = [
    `analysis`,
    `annotations`,
    `anomalies`,
    `behaviors`,
    `billing`,
    `bookmarks`,
    `cohorts`,
    `collect_everything_events`,
    `correlate`,
    `custom_alerts`,
    `custom_events`,
    `custom_properties`,
    `dashboard_reports`,
    `data_definitions`,
    `data_groups`,
    `datasets`,
    `engage`,
    `erf_experiments`,
    `events`,
    `experiments`,
    `feature_flags`,
    `flows`,
    `formulas`,
    `funnels`,
    `heat_maps`,
    `playlists`,
    `impact`,
    `insights`,
    `integrations`,
    `journeys`,
    `jql`,
    `learning_action`,
    `live`,
    `messages`,
    `metrics`,
    `metric_trees`,
    `mobile`,
    `nps_survey`,
    `organizations`,
    `projects`,
    `retention`,
    `segmentation`,
    `stream`,
    `themes`,
    `user_details`,
    `visited_report`,
    `visual_journeys`,
  ];

  /* eslint-disable camelcase */
  const oAuthParams = {
    client_id: mixpanelClientId,
    redirect_uri: redirectUri,
    response_type: `token`,
    scope: oauthScopes.join(` `),
    state: JSON.stringify({state: redirectState}),
  };

  // localhost needs to use mixpanel.com as oauth host so that it can redirect to login if needed
  const oauthHost = isLocalhostDevEnv() ? `https://mixpanel.com` : apiHost();
  return `${oauthHost}/oauth/authorize/?${queryString.stringify(oAuthParams)}`;
}

function getCachedStandaloneAccessToken(): string {
  // prod env should always fetch new token, no caching
  if (isDevEnv() && isStandalone() && typeof localStorage !== `undefined`) {
    const ttlMs = 24 * 60 * 60 * 1000; // one day expiry
    const accessToken = localStorage.getItem(`access_token`);
    const timestamp = localStorage.getItem(`access_token_timestamp`);

    if (accessToken && timestamp && parseInt(timestamp, 10) > Date.now() - ttlMs) {
      return accessToken;
    }
  }

  return null;
}

function updateCachedStandaloneAccessToken(accessToken: string): string {
  if (isDevEnv() && isStandalone() && typeof localStorage !== `undefined`) {
    localStorage.setItem(`access_token`, accessToken || ``);
    localStorage.setItem(`access_token_timestamp`, String(Date.now()));
  }
  return accessToken;
}

// ----- main ApiClient class ---- ///

/**
 * Wrapper for fetch which includes access token refreshes.
 */
class ApiClient extends HttpClient {
  private declare authRetries: number;
  private declare accessToken: string;
  public declare useSessionAuth: boolean;

  constructor() {
    super();
    this.authRetries = 0;
    this.pendingResponseCache = new Map();
    this.useSessionAuth = isTestEnv() || (isSameOrigin() && !accessTokenFromURLHash());
  }

  get token() {
    return this.accessToken;
  }

  public async fetch(url: string, options: FetchOptions = {}): Promise<Response> {
    if (!this.useSessionAuth && !this.token && !options.fromFetchCache && !options.skipAccessToken) {
      await this.getAccessToken();
    }
    const fetchCall = this._fetch(url, options);
    if (options.minExecutionTime) {
      await Promise.all([fetchCall, sleep(options.minExecutionTime)]);
    }
    return fetchCall;
  }

  public async getAccessToken(): Promise<string> {
    if (!this.token) {
      // access token can come in directly via location hash params e.g from dashboard-digest -> puppetter screenshotter
      if (accessTokenFromURLHash() || !isSameOrigin()) {
        await this._fetchAccessTokenViaRedirect();
      } else {
        await this._fetchAccessTokenViaApi();
      }
    }
    return this.token;
  }

  private _fetchAccessTokenViaApi(): Promise<void> {
    const url = this._urlWithApiHost(`/oauth/access_token/`);
    const fetchInit: RequestInit = {credentials: `include`};
    const pendingResponseCacheKey = stableStringify({url, fetchInit});

    // if we've already made a request fetch access_token, don't make another one, wait for existing request
    if (this.pendingResponseCache.has(pendingResponseCacheKey)) {
      return this.pendingResponseCache.get(pendingResponseCacheKey);
    }

    reporter.warn(`fetching access_token via ${url} is deprecated`);

    const pendingRefresh = window
      .fetch(url, fetchInit)
      .then(async (response) => {
        // actually follow redirects to login page, but change 'next' to current page
        if (response.redirected) {
          const loginMatch = /.+\/login\/\?next=/.exec(response.url);
          if (loginMatch) {
            await waitForRedirect(`${loginMatch[0]}${window.location.href}`);
          }
        }

        if (!response.ok) {
          throw makeError(response);
        }

        const jsonResponse = await response.json();
        // In the case that the user is legitimately logged out, the oauth token
        // request 302s to the normal html login page, which 200s, and then
        // response.json() returns true. I wish it would fail instead, but for
        // now there's this check.
        if (!jsonResponse.token) {
          throw new ResponseError(jsonResponse, `No token in response`);
        }

        this.accessToken = jsonResponse.token;
      })
      .finally(() => this.pendingResponseCache.delete(pendingResponseCacheKey));

    this.pendingResponseCache.set(pendingResponseCacheKey, pendingRefresh);
    return pendingRefresh;
  }

  /**
   * The default way of fetching the access_token is exchanging session_id + x-csrftoken header via /oauth/access_token.
   * However calling /oauth/access_token doesn't work cross domain.
   * For cross-domain access_token fetching we have to do an oauth redirect to /oauth/authorize which redirects back to
   * the page with #access_token=... in location.hash, and we read it from there.
   * Devboxes, staging and prod are same-origin, however standalone is on localhost so it goes via this route.
   */
  private async _fetchAccessTokenViaRedirect(): Promise<void> {
    const parsedHash = queryString.parse(window.location.hash);
    const parsedUrlParams = queryString.parse(window.location.search);
    const oAuthState: OAuthState = Object.assign(
      {numRedirectAttempts: 0, originalHash: ``},
      parsedHash.state ? JSON.parse(decodeURIComponent(parsedHash.state)) : null,
    );
    const numRedirectAttempts = oAuthState.numRedirectAttempts;
    const accessToken = parsedHash[`access_token`] || getCachedStandaloneAccessToken();

    if (accessToken) {
      this.accessToken = updateCachedStandaloneAccessToken(accessToken);

      // replace the hash with originalHash or clear it completely, so #access_token isn't visible on the location bar
      if (parsedHash[`access_token`]) {
        window.location.hash = oAuthState.originalHash || ``;
      }
    } else {
      if (parsedUrlParams[`error`]) {
        throw new Error(`Authorization error: ${parsedUrlParams[`error`]}`);
      }

      if (numRedirectAttempts < MAX_AUTH_RETRIES) {
        await waitForRedirect(buildOAuthRedirectUrl({...oAuthState, numRedirectAttempts: numRedirectAttempts + 1}));
      } else {
        throw new Error(`Authorization failed, ${this.authRetries} retries`);
      }
    }
  }

  // TODO opslead
  protected async _fetch(url: string, fetchOptions: FetchOptions = {}): Promise<Response> {
    trackQueryApiFetch(url);

    const startTime = new Date().getTime();

    url = this._urlWithApiHost(url);

    // Proactive region check for query API requests
    // This prevents requests from being sent to the wrong region and failing with 404
    if (isMixpanelDotCom() && url.includes(`/api/query/`)) {
      const projectDomain = getCachedProjectDomain();
      const currentHostname = window.location.hostname;

      // If we have the project domain cached and it doesn't match our current hostname,
      // redirect to the correct region before making the request
      // Note: For local dev, projectDomain will be "" so we won't redirect
      if (projectDomain && projectDomain !== currentHostname) {
        // Construct the redirect URL using the project's domain
        const redirectUrl = new URL(window.location.href);
        redirectUrl.hostname = projectDomain;

        // Preserve the hash if we're on the same pathname
        const redirectLocation =
          redirectUrl.pathname === window.location.pathname
            ? new URL(window.location.hash, redirectUrl).href
            : redirectUrl.href;

        // Report that we're redirecting due to region mismatch (for monitoring)
        reportQueryApiRequestsToWrongRegion({redirectRegion: redirectUrl.origin, reactive: false});

        // Redirect to the correct region
        await waitForRedirect(redirectLocation);

        // This code won't be reached, but TypeScript doesn't know that
        throw new Error(`Redirecting to correct region: ${projectDomain}`);
      }
    }

    const {
      headers = {},
      method = `GET`,
      body = null,
      params = {},
      throwOnInvalidStatus = false,
      abortSignal: signal = null,
    } = fetchOptions || {};

    let authHeader = {};
    if (!fetchOptions.skipAccessToken) {
      authHeader = {Authorization: this.useSessionAuth ? `Session` : `Bearer ${this.token}`};
    }

    if (fetchOptions.importance) {
      params[`importance`] = fetchOptions.importance;
    }

    const requestID = uuidv4();

    const fetchInit: RequestInit = {
      headers: {
        ...authHeader,
        ...headers,
        'Request-URL': window.location.href,
        'Project-ID': getProjectId(),
        'Request-ID': requestID,
      },
      method,
      body,
      signal,
      ...params,
    };

    const pendingResponseCacheKey = stableStringify({
      url,
      fetchInit: omit(fetchInit, `headers.Request-ID`),
      fromFetchCache: fetchOptions.fromFetchCache,
    });
    // for requests with abort signal, avoid responding with responseCache as for 1st request's signal will cancel 2nd one
    if (!signal && this.pendingResponseCache.has(pendingResponseCacheKey)) {
      // we clone the response because body can only be read once.
      // multiple callers calling response.json() will fail with 'body stream is locked' error
      return (
        this.pendingResponseCache
          .get(pendingResponseCacheKey)
          // return fetch Cache miss as null
          .then((response) => (response ? response.clone() : null))
      );
    }

    let responsePromise: Promise<Response>;
    if (fetchOptions.fromFetchCache) {
      responsePromise = fromFetchCache(
        filterSearchParamsForCache(url),
        omit(fetchInit, `headers.Authorization`, `headers.Request-ID`),
        fetchOptions.fromFetchCacheTtl,
      )
        .then(({cachedResponse, trackingDict}) => {
          if (cachedResponse) {
            const endTime = new Date().getTime();
            trackApiClientFetch(
              cachedResponse,
              {latencyMs: endTime - startTime, cached: true, method},
              {...trackingDict, requestID},
            );
          }
          return cachedResponse;
        })
        .finally(() => this.pendingResponseCache.delete(pendingResponseCacheKey));
    } else {
      // Use the ROOT_CONTEXT here, because we want the underlying fetch requests to be root spans of their own trace
      // rather than child spans of the app-level span
      otel_context.with(ROOT_CONTEXT, () => {
        responsePromise = window
          .fetch(url, fetchInit)
          .then(async (response) => {
            const endTime = new Date().getTime();
            trackApiClientFetch(response, {latencyMs: endTime - startTime, cached: false, method}, {requestID});

            if (response.status === StatusCode.NotFound) {
              // HTTP hack: 302 API responses across our US/EU subdomains will
              // get rejected by browser before ApiClient can follow them,
              // so instead we return 404 with a custom reponse header to
              // tell ApiClient where to switch the page to
              const baseLocation = response.headers.get(`mp-base-location`);
              if (baseLocation) {
                reportQueryApiRequestsToWrongRegion({redirectRegion: baseLocation, reactive: true});
                const redirectUrl = new URL(baseLocation);
                const redirectLocation =
                  redirectUrl.pathname === window.location.pathname
                    ? new URL(window.location.hash, redirectUrl).href
                    : baseLocation;
                await waitForRedirect(redirectLocation);
                return response;
              }
            }

            if (throwOnInvalidStatus && response.status >= StatusCode.BadRequest) {
              throw makeError(response);
            }

            if (response.status >= StatusCode.InternalServerError) {
              // we always throw for 500-ish error
              this.authRetries = 0;
              throw makeError(response);
            } else if (response.status === StatusCode.Unauthenticated) {
              this.accessToken = updateCachedStandaloneAccessToken(null);

              if (++this.authRetries < MAX_AUTH_RETRIES) {
                // before making another internal fetch, delete current request from pendingResponseCache
                this.pendingResponseCache.delete(pendingResponseCacheKey);
                return this.fetch(url, fetchOptions);
              }
              throw new Error(`Authorization failed, ${this.authRetries} retries`);
            } else if (response.redirected) {
              await waitForRedirect(response.url);
              return response;
            } else {
              // underlying updateFetchCache will only update if response.ok and response.status === 200
              if (fetchOptions.updateFetchCache) {
                updateFetchCache(
                  filterSearchParamsForCache(url),
                  omit(fetchInit, `headers.Authorization`, `headers.Request-ID`),
                  response,
                );
              }
              this.authRetries = 0;
              return response;
            }
          })
          .finally(() => this.pendingResponseCache.delete(pendingResponseCacheKey));
      });
    }

    this.pendingResponseCache.set(pendingResponseCacheKey, responsePromise);
    return responsePromise;
  }
}

let apiClient: ApiClient;
if (isBrowserEnv() && !isTestEnv()) {
  // since api-client is a prime consumer of fetch-cache
  // we ensure fetch-cache is namespaced by user.id before fromFetchCache and updateFetchCache is called
  const user = userCookieUtils.getUserIdentityFromCookie();
  if (user) {
    setCacheNamespace(`user:${user.id}`);
  }

  // initialize as window singleton that is shared between bundles
  apiClient = window[`__apiClient`] = window[`__apiClient`] || new ApiClient();
} else {
  // probably in unit test env, module level singleton
  apiClient = new ApiClient();
}

export {apiClient, ApiClient};
